import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { addDays, semesterWeek, type ISODate } from '@app/shared'
import { connectorIsLive } from '@app/shared'

import {
  isDemoMode,
  useAssignments,
  useCalendarRange,
  useClassOccurrences,
  useClock,
  useConnectors,
  useCourses,
  useCreateMailRule,
  useDayTriage,
  useMailAudit,
  useStartGoogleMailConnect,
  useTasks,
  useTerm,
} from '../lib/data'
import { demoDayContext } from '../lib/data/demo'
import type { DayScenario, MailTriageItem, MailVerdict } from '../lib/data/types'
import { DAY_EDGE_MS } from '../lib/motion'
import { isInAppPath } from '../components/connections/mail'
import { useDayController, type DayItem, type Section } from '../components/day/controller'
import { EmptyLine, ItemCard, openItem, withGhosts, type DayCtx } from '../components/day/cards'
import {
  PILE_ORDER,
  assignmentsDueToday,
  attentionLine,
  auditWeekStart,
  backLabel,
  clock24,
  comingUp,
  connectorChip,
  dayClock24,
  dayLabel,
  foldSummary,
  headerSummary,
  mailSourceChip,
  needsMix,
  orderAccounts,
  pickedPhrase,
  pickedSnoozeUntil,
  quietLine,
  remindersDueToday,
  snoozeChoices,
  somedayCount,
  stillWaiting,
  typingInto,
  type SourceChip,
} from '../components/day/designed'
import { presetPhrase } from '../components/day/cards'
import { FoldedCard, type FoldPile } from '../components/day/folded'
import { ClockGlyph, MailGlyph, Sunrise } from '../components/day/icons'
import { stepPick } from '../components/day/menus'
import { isStepsKey } from '../components/day/proposals'
import { DayCard, RollingCount, SecHead, Sk } from '../components/day/parts'
import {
  BandTitle,
  ComingSkeleton,
  ComingUp,
  DayBand,
  DaySkeleton,
  DisconnectedBanner,
  FirstRunSources,
  FreshChip,
  HandledButton,
  Hero,
  NeedsSkeleton,
  ShortcutSheet,
  StaleBanner,
  TopBar,
  UndoToast,
  WaitingSkeleton,
  WeeklyCard,
  YourDay,
  handledAt,
  type DayEvent,
  type FirstRunSource,
} from '../components/day/sections'

// ============================================================================
// THE DAY PAGE — docs/design/handoff/day-page/ (Lane 4a), desktop.
//
// One screen read every morning: what needs me today (mail sorted by Claude across three inboxes,
// bCourses work due today, reminders due today), what is important, what is still waiting from
// earlier days, the folded piles, and — beside it — the day and the next seven days.
// Column order: Needs you today, Important, Not sorted yet, Still waiting, Folded. Important sits
// directly under Needs you today so a pile move visibly lands one section down (owner, 2026-11-01).
//
// THE HANDOFF IS LAW (CLAUDE.md → Design system): exact strings from the boards, tokens only,
// every state on the board table. Where the API has no field for something a board draws, the
// page draws nothing there — hidden is not missing, and absent is absent.
//
// The phone keeps the prototype (routes/DayPrototype.tsx) while the owner redesigns mobile.
// ============================================================================

const SCENARIOS: DayScenario[] = ['morning', 'quiet', 'degraded', 'first-run', 'loading']
const WEEKLY_DONE_KEY = (weekStart: ISODate) => `lifeos.day.weekly.${weekStart}`

function weeklyDone(weekStart: ISODate | undefined): boolean {
  if (!weekStart) return false
  try {
    return window.localStorage.getItem(WEEKLY_DONE_KEY(weekStart)) !== null
  } catch {
    return false
  }
}

export function markWeeklyDone(weekStart: ISODate) {
  try {
    window.localStorage.setItem(WEEKLY_DONE_KEY(weekStart), 'done')
  } catch {
    // A browser that refuses storage shows the card again next time. Nothing is lost.
  }
}

export function useDayScenario(): DayScenario | null {
  const [params] = useSearchParams()
  const raw = params.get('state') as DayScenario | null
  return isDemoMode && raw && SCENARIOS.includes(raw) ? raw : null
}

const mailItem = (m: MailTriageItem): DayItem => ({ key: `mail:${m.id}`, kind: 'mail', title: m.subject || '(no subject)', url: m.url, mail: m })

export function Day() {
  const scenario = useDayScenario()
  const forceLoading = scenario === 'loading'
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const clock = useClock({ live: true })
  const today = clock.data?.date
  const nowMinutes = clock.data?.minutesIntoDay ?? 0
  const nowRef = useRef(nowMinutes)
  nowRef.current = nowMinutes

  const query = useMemo(() => ({ days: 7, ...(scenario ? { scenario } : {}) }), [scenario])
  const triage = useDayTriage(query)
  const override = isDemoMode ? demoDayContext(scenario) : null
  const assignmentsQ = useAssignments({ openOnly: true })
  const tasksQ = useTasks({ done: false })
  const classesQ = useClassOccurrences(today)
  const rangeQ = useCalendarRange(today ? { from: today, to: today } : undefined)
  const connectorsQ = useConnectors()
  const coursesQ = useCourses()
  const termQ = useTerm()
  const weekStart = today ? auditWeekStart(today) : undefined
  const audit = useMailAudit(weekStart, scenario)
  const createRule = useCreateMailRule()
  const connect = useStartGoogleMailConnect()

  const ctrl = useDayController(query, () => nowRef.current)
  const [openPile, setOpenPile] = useState<string | null>(params.get('open'))
  const [sheet, setSheet] = useState(false)
  const [pick, setPick] = useState<ISODate>(today ? addDays(today, 1) : ('2000-01-01' as ISODate))
  const [entering, setEntering] = useState<Set<string>>(() => new Set())
  const rootRef = useRef<HTMLDivElement>(null)

  // ── Inputs ──
  const assignments = override?.assignments ?? assignmentsQ.data ?? []
  const tasks = override?.tasks ?? tasksQ.data ?? []
  const t = triage.data
  const accounts = useMemo(() => (t ? orderAccounts(t.accounts) : []), [t])

  // ── 14e: new mail never pushes the list. Ids first seen after load wait behind a pill. ──
  const known = useRef<Set<string> | null>(null)
  if (t && known.current === null) known.current = new Set(t.groups.needsYouToday.map((m) => m.id))
  for (const id of ctrl.movedNow) known.current?.add(id)
  const held = t ? t.groups.needsYouToday.filter((m) => !known.current!.has(m.id)) : []
  const showHeld = () => {
    const ids = held.map((m) => `mail:${m.id}`)
    for (const m of held) known.current!.add(m.id)
    setEntering(new Set(ids))
    setTimeout(() => setEntering(new Set()), DAY_EDGE_MS)
  }

  const needs: DayItem[] = useMemo(() => {
    if (!t || !today) return []
    const mail = t.groups.needsYouToday.filter((m) => known.current?.has(m.id)).map(mailItem)
    const due = assignmentsDueToday(assignments, today).map(({ assignment: a }) => ({
      key: `assignment:${a.id}`,
      kind: 'assignment' as const,
      title: a.title,
      url: a.htmlUrl,
      assignment: a,
    }))
    const rem = remindersDueToday(tasks, today).map((task) => ({ key: `reminder:${task.id}`, kind: 'reminder' as const, title: task.title, url: null, task }))
    return [...mail, ...due, ...rem].filter((i) => !ctrl.hidden.has(i.key))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, today, assignments, tasks, ctrl.hidden, held.length])

  const waiting = useMemo(
    () => (today ? stillWaiting(assignments, tasks, today).filter((w) => !ctrl.hidden.has(w.key)) : []),
    [assignments, tasks, today, ctrl.hidden],
  )

  // The quiet board is the state the page LOADS into; a list emptied by hand keeps its section and
  // says "Nothing else needs you today." (14g — absence is a fact, not a reveal).
  const quietAtLoad = useRef<boolean | null>(null)
  // "Answered" includes a refusal: a read that failed is not a read still coming.
  const sourcesReady = Boolean(t) && (Boolean(override) || (!assignmentsQ.isPending && !tasksQ.isPending))
  if (quietAtLoad.current === null && sourcesReady && !forceLoading) quietAtLoad.current = needs.length === 0
  const firstRun = Boolean(t) && accounts.length === 0
  const quiet = !firstRun && quietAtLoad.current === true && needs.length === 0 && held.length === 0

  // ── Sources ──
  const chips: SourceChip[] = useMemo(() => {
    if (!today) return []
    const out = accounts.map((a) => mailSourceChip(a, today, nowMinutes))
    for (const [id, name] of [
      ['canvas', 'bCourses'],
      ['gcal', 'Calendar'],
    ] as const) {
      const c = connectorsQ.data?.find((x) => x.id === id)
      if (c && connectorIsLive(c)) out.push(connectorChip(id, name, c.lastSyncAt, c.status !== 'ok', today, nowMinutes))
    }
    return out
  }, [accounts, connectorsQ.data, today, nowMinutes])
  const chipByAccount = useMemo(() => new Map(chips.map((c) => [c.key, c])), [chips])

  // ── Events ──
  const events: DayEvent[] = useMemo(() => {
    if (!today) return []
    if (override) return override.classes.map((c) => ({ key: `class-${c.courseId}-${c.startMinutes}`, start: c.startMinutes, end: c.endMinutes, title: c.courseCode, src: [c.location, 'class'] }))
    const classes = (classesQ.data ?? []).map((c) => ({
      key: `class-${c.courseId}-${c.startMinutes}`,
      start: c.startMinutes,
      end: c.endMinutes,
      title: `${c.courseCode} — ${c.kind === 'lecture' ? 'Lecture' : c.kind === 'discussion' ? 'Discussion' : c.kind === 'lab' ? 'Lab' : c.kind}`,
      src: [c.location, 'class'].filter(Boolean),
    }))
    const blocks = (rangeQ.data?.blocks ?? [])
      .filter((b) => b.date === today && b.source !== 'canvas' && b.state === 'accepted' && b.status !== 'cancelled')
      .map((b) => ({
        key: `block-${b.id}`,
        start: b.startMinutes,
        end: b.endMinutes,
        title: b.title,
        src: [b.location ?? '', b.source === 'google' ? 'from Google' : 'from LifeOS'].filter(Boolean),
      }))
    const allDay = (rangeQ.data?.allDayEvents ?? [])
      .filter((e) => e.date <= today && today <= e.endDate && e.status !== 'cancelled')
      .map((e) => ({ key: `allday-${e.id}`, start: null, end: null, title: e.title, src: ['from Google'] }))
    return [...allDay, ...[...classes, ...blocks].sort((a, b) => (a.start ?? 0) - (b.start ?? 0))]
  }, [today, override, classesQ.data, rangeQ.data])

  const coming = useMemo(() => (today ? comingUp(assignments, today) : null), [assignments, today])

  // ── Section models ──
  const unsorted = (t?.groups.unsorted ?? []).map(mailItem)
  const important = (t?.groups.important ?? []).map(mailItem)
  const lastSort = useMemo(() => {
    if (!t) return null
    const all = [...t.groups.needsYouToday, ...t.groups.important, ...t.groups.canWait, ...t.groups.noise]
    const times = all.filter((m) => m.triageSource === 'model' && m.triagedAt).map((m) => m.triagedAt!)
    return times.sort().at(-1) ?? null
  }, [t])

  const weeklyReady = Boolean(audit.data && audit.data.sample.length > 0 && !weeklyDone(weekStart))

  const ctx: DayCtx | null = today
    ? {
        ctrl,
        today,
        nowMinutes,
        chips: chipByAccount,
        pick,
        setPick,
        onRule: (item, verdict) => {
          const mail = item.mail
          if (!mail) return
          ctrl.setMenu(null)
          createRule.mutate({ matchType: 'address', matchValue: mail.from.address.toLowerCase(), verdict })
        },
      }
    : null

  // The registry the keyboard reads: which card is which, in which section, at which index.
  const registry = useRef(new Map<string, { item: DayItem; section: Section; index: number }>())
  registry.current = new Map()
  const reg = (section: Section, list: DayItem[]) => list.forEach((item, index) => registry.current.set(item.key, { item, section, index }))
  reg('needs', needs)
  reg('unsorted', unsorted)
  reg('important', important)
  reg(
    'waiting',
    waiting.map((w) => ({ key: w.key, kind: w.kind, title: w.title, url: w.url, task: w.task, assignment: w.assignment })),
  )
  const foldItems = {
    snoozed: (t?.groups.snoozed ?? []).map(mailItem),
    can_wait: (t?.groups.canWait ?? []).map(mailItem),
    noise: (t?.groups.noise ?? []).map(mailItem),
  }
  reg('snoozed', foldItems.snoozed)
  reg('can_wait', foldItems.can_wait)
  reg('noise', foldItems.noise)

  // ── The keyboard (Keyboard board): J/K D S X O 1–4 R Enter W Z ? Esc, and C in the snooze menu ──
  const focusNav = useCallback((delta: 1 | -1) => {
    const root = rootRef.current
    if (!root) return
    const nodes = [...root.querySelectorAll<HTMLElement>('[data-day-nav]')]
    if (nodes.length === 0) return
    const at = nodes.findIndex((n) => n === document.activeElement || n.contains(document.activeElement))
    const next = at === -1 ? (delta === 1 ? 0 : nodes.length - 1) : Math.min(nodes.length - 1, Math.max(0, at + delta))
    nodes[next]!.focus()
    nodes[next]!.scrollIntoView?.({ block: 'nearest' })
  }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (typingInto(e.target)) return
      const key = e.key
      if (sheet) {
        if (key === 'Escape' || key === '?') {
          e.preventDefault()
          setSheet(false)
        }
        return
      }
      const menu = ctrl.menu
      const focused = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>('[data-day-key]')
      const entry = menu ? registry.current.get(menu.key) : focused ? registry.current.get(focused.dataset.dayKey ?? '') : undefined
      if (menu && entry && today) {
        if (key === 'Escape') {
          e.preventDefault()
          ctrl.setMenu(menu.kind === 'picker' ? { key: menu.key, kind: 'snooze' } : null)
          return
        }
        if (menu.kind === 'snooze') {
          const choice = snoozeChoices(today, nowMinutes).find((c) => String(c.key) === key)
          if (choice) {
            e.preventDefault()
            ctrl.snooze(entry.item, entry.section, entry.index, choice.until, presetPhrase(choice))
            return
          }
          if (key.toLowerCase() === 'c') {
            e.preventDefault()
            setPick(addDays(today, 1))
            ctrl.setMenu({ key: menu.key, kind: 'picker' })
            return
          }
        }
        if (menu.kind === 'picker') {
          if (key.startsWith('Arrow')) {
            e.preventDefault()
            setPick((p) => stepPick(p, key, today))
            return
          }
          if (key === 'Enter' && entry.item.mail) {
            e.preventDefault()
            const until = pickedSnoozeUntil(pick, { kind: 'mail', receivedAt: entry.item.mail.receivedAt })
            ctrl.snooze(entry.item, entry.section, entry.index, until, pickedPhrase(until))
            return
          }
        }
        return
      }
      switch (key) {
        case 'j':
        case 'J':
          e.preventDefault()
          focusNav(1)
          return
        case 'k':
        case 'K':
          e.preventDefault()
          focusNav(-1)
          return
        case '?':
          e.preventDefault()
          setSheet(true)
          return
        case 'z':
        case 'Z':
          if (ctrl.toast?.undo) {
            e.preventDefault()
            ctrl.undo()
          }
          return
        case 'w':
        case 'W':
          if (weeklyReady) {
            e.preventDefault()
            navigate(scenario ? `/day/weekly?state=${scenario}` : '/day/weekly')
          }
          return
        case 'Escape':
          if (openPile) {
            e.preventDefault()
            const btn = rootRef.current?.querySelector<HTMLElement>(`[data-day-fold="${openPile}"]`)
            setOpenPile(null)
            btn?.focus()
          }
          return
      }
      if (!entry) return
      const { item, section, index } = entry
      const lower = key.toLowerCase()
      const step = () => focusNav(1)
      if (lower === 'd' && (item.kind === 'mail' || item.kind === 'reminder')) {
        e.preventDefault()
        step()
        ctrl.finish(item, section, index, 'done')
      } else if (lower === 'x' && item.kind === 'mail') {
        e.preventDefault()
        step()
        ctrl.finish(item, section, index, 'dismiss')
      } else if (lower === 's' && item.kind === 'mail' && section !== 'snoozed') {
        e.preventDefault()
        ctrl.setMenu({ key: item.key, kind: 'snooze' })
      } else if (lower === 'r' && item.kind === 'mail') {
        e.preventDefault()
        ctrl.setMenu({ key: item.key, kind: 'rules' })
      } else if (isStepsKey(key, item.kind)) {
        e.preventDefault()
        ctrl.toggleSteps(item.key)
      } else if (lower === 'o') {
        e.preventDefault()
        openItem(item)
      } else if (['1', '2', '3', '4'].includes(key) && item.kind === 'mail' && section !== 'snoozed') {
        e.preventDefault()
        const verdict = PILE_ORDER[Number(key) - 1] as MailVerdict
        if (item.mail?.verdict !== verdict) {
          step()
          ctrl.move(item, section, index, verdict)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [sheet, ctrl, today, nowMinutes, pick, focusNav, openPile, weeklyReady, navigate, scenario])

  // A click anywhere outside an open menu closes it.
  useEffect(() => {
    if (!ctrl.menu) return
    const close = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('[role="menu"]') || target.closest('[aria-expanded="true"]')) return
      ctrl.setMenu(null)
    }
    window.addEventListener('mousedown', close)
    return () => window.removeEventListener('mousedown', close)
  }, [ctrl])

  // When the picker opens from the mouse, it starts on tomorrow.
  useEffect(() => {
    if (ctrl.menu?.kind === 'picker' && today && pick < today) setPick(addDays(today, 1))
  }, [ctrl.menu, today, pick])

  // ── Reconnect / connect: the Gmail card's own flow ──
  const startConnect = () =>
    connect.mutate(undefined, {
      onSuccess: (url) => {
        if (isInAppPath(url)) navigate(url, { replace: true })
        else window.location.assign(url)
      },
    })

  // ── Render ──
  const term = termQ.data
  const week = today && term ? (semesterWeek(today, term.instructionStarts) > 0 ? `Week ${semesterWeek(today, term.instructionStarts)} · ${term.name}` : term.name) : null
  const loadingMail = forceLoading || triage.isPending
  const attention = attentionLine(chips)

  const disconnected = chips.filter((c) => c.state === 'disconnected' && accounts.some((a) => a.id === c.key))
  const stale = chips.filter((c) => c.state === 'stale' && accounts.some((a) => a.id === c.key))
  const freshInboxes = accounts.length - disconnected.length - stale.length

  const summary = loadingMail ? (
    <Sk w={180} h={10} />
  ) : (
    <span className="text-[11.5px] text-muted">
      {headerSummary({ needs: needs.length + held.length, unsorted: unsorted.length, waiting: waiting.length, events: events.length, noInboxes: firstRun })}
    </span>
  )
  const handledCount = t?.counts.handledToday
  const bandRight: ReactNode = loadingMail ? (
    ['Professional', 'Berkeley', 'Flexible', 'bCourses', 'Calendar'].map((n) => <FreshChip key={n} chip={{ key: n, name: n, state: 'loading', value: '', error: null, lastSyncAt: null }} />)
  ) : firstRun ? null : (
    <>
      {chips.map((c) => (
        <FreshChip key={c.key} chip={c} />
      ))}
      {handledCount !== undefined && (handledCount > 0 || ctrl.handled.length > 0) && <HandledButton count={handledCount} rows={ctrl.handled} nowLabel={handledAt} />}
    </>
  )

  const remindersToday = today ? remindersDueToday(tasks, today).length : 0
  const someday = somedayCount(tasks)
  const dayLoading = forceLoading || (!override && (classesQ.isPending || rangeQ.isPending))
  const comingLoading = forceLoading || (!override && assignmentsQ.isPending)

  const yourDay = dayLoading ? (
    <DaySkeleton />
  ) : (
    <YourDay events={events} nowMinutes={nowMinutes} remindersToday={remindersToday} someday={someday} showNow={!firstRun} />
  )
  const comingCard =
    comingLoading || !coming ? (
      <ComingSkeleton />
    ) : (
      <ComingUp days={coming.days} count={coming.count} to={coming.to} quietTail={coming.quietTail} toLabel={dayLabel(coming.to)} />
    )

  const waitingCard =
    ctx && waiting.length > 0 ? (
      <DayCard aria-labelledby="h-wait">
                  <SecHead
                    id="h-wait"
                    icon={<ClockGlyph className="text-tasks" />}
                    title="Still waiting"
                    count={<RollingCount value={waiting.length} />}
                    note="from earlier days · oldest first · nothing expires"
                  />
                  {waiting.map((w, index) => (
                    <ItemCard
                      key={w.key}
                      item={{ key: w.key, kind: w.kind, title: w.title, url: w.url, task: w.task, assignment: w.assignment }}
                      section="waiting"
                      index={index}
                      ctx={ctx}
                      waiting={w}
                    />
                  ))}
                </DayCard>
    ) : null

  return (
    <div ref={rootRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-page" aria-busy={loadingMail || undefined}>
      {today && <TopBar today={today} week={week} attention={attention} />}
      <DayBand title={<BandTitle>Day</BandTitle>} summary={summary} right={bandRight} />

      <main className="flex flex-wrap items-start gap-[18px] px-[30px] pb-24 pt-[18px]">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-[18px]">
          {loadingMail || !ctx || !today ? (
            <>
              <NeedsSkeleton />
              <WaitingSkeleton />
            </>
          ) : triage.error ? (
            <DayCard className="px-4 py-[14px]">
              <p className="m-0 text-[12px] leading-[1.45] text-danger text-pretty">
                {triage.error instanceof Error && triage.error.message ? triage.error.message : 'The sorted mail could not be loaded.'}
              </p>
              <button type="button" className="day-btn mt-3" onClick={() => void triage.refetch()} disabled={triage.isFetching}>
                {triage.isFetching ? 'Retrying…' : 'Retry'}
              </button>
            </DayCard>
          ) : firstRun ? (
            <>
              <Hero
                compact
                title="Connect your inboxes to see what needs you."
                body="The Day page reads three Google inboxes, sorts new mail into four piles, and puts it beside your classes and deadlines. It only reads. Nothing is sent, archived, labelled or deleted in Gmail."
              />
              <FirstRunSources
                sources={firstRunSources(connectorsQ.data ?? [], coursesQ.data?.length ?? null, today)}
                onConnect={startConnect}
                busy={connect.isPending}
                error={connect.error instanceof Error ? connect.error.message : null}
              />
              {/* "Classes, deadlines and reminders already show here" — so work due today and what
                  is still waiting from earlier days are listed even before any inbox is connected. */}
              {needs.length > 0 && (
                <DayCard aria-labelledby="h-needs">
                  <SecHead id="h-needs" icon={<Sunrise className="text-urgent-time" />} title="Needs you today" count={<RollingCount value={needs.length} />} note={needsMix(0, needs.filter((i) => i.kind === 'assignment').length, needs.filter((i) => i.kind === 'reminder').length)} />
                  {withGhosts(needs, ctrl.ghosts('needs')).map(({ item, index }) => (
                    <ItemCard key={item.key} item={item} section="needs" index={index} ctx={ctx} />
                  ))}
                </DayCard>
              )}
              {waitingCard}
            </>
          ) : (
            <>
              {disconnected.map((c) => {
                const account = accounts.find((a) => a.id === c.key)!
                const others = freshInboxes === 0 ? null : freshInboxes === 1 ? 'The other inbox is current.' : `The other ${freshInboxes === 2 ? 'two' : freshInboxes} inboxes are current.`
                return (
                  <DisconnectedBanner
                    key={c.key}
                    name={c.name}
                    address={account.address}
                    since={c.lastSyncAt ? sinceWords(c.lastSyncAt, today) : null}
                    others={others}
                    onReconnect={startConnect}
                    busy={connect.isPending}
                  />
                )
              })}

              {quiet ? (
                <Hero
                  title="Nothing needs you today."
                  body={quietLine(accounts.length - disconnected.length, lastSort, waiting.length)}
                />
              ) : (
                <DayCard aria-labelledby="h-needs">
                  <SecHead
                    id="h-needs"
                    icon={<Sunrise className="text-urgent-time" />}
                    title="Needs you today"
                    count={<RollingCount value={needs.length} />}
                    note={needs.length > 0 ? needsMix(needs.filter((i) => i.kind === 'mail').length, needs.filter((i) => i.kind === 'assignment').length, needs.filter((i) => i.kind === 'reminder').length) : undefined}
                  >
                    {held.length > 0 && (
                      <button type="button" onClick={showHeld} className="day-fadein h-[22px] cursor-pointer rounded-pill border border-accent-line bg-accent-fill px-[9px] font-[inherit] text-[11px] font-semibold text-accent">
                        {held.length} new · show
                      </button>
                    )}
                  </SecHead>
                  {withGhosts(needs, ctrl.ghosts('needs')).map(({ item, index }) => (
                    <ItemCard key={item.key} item={item} section="needs" index={index} ctx={ctx} enter={entering.has(item.key)} />
                  ))}
                  {needs.length === 0 && ctrl.ghosts('needs').length === 0 && <EmptyLine>Nothing else needs you today.</EmptyLine>}
                </DayCard>
              )}

              {!quiet && important.length + ctrl.ghosts('important').length > 0 && (
                <DayCard aria-labelledby="h-imp">
                  <SecHead id="h-imp" icon={<MailGlyph className="text-info" />} title="Important" count={<RollingCount value={important.length} />} note="nothing to act on yet" />
                  {withGhosts(important, ctrl.ghosts('important')).map(({ item, index }) => (
                    <ItemCard key={item.key} item={item} section="important" index={index} ctx={ctx} />
                  ))}
                </DayCard>
              )}

              {unsorted.length > 0 && (
                <DayCard aria-labelledby="h-uns">
                  <SecHead id="h-uns" icon={<MailGlyph dashed className="text-muted" />} title="Not sorted yet" count={<RollingCount value={unsorted.length} />} />
                  {withGhosts(unsorted, ctrl.ghosts('unsorted')).map(({ item, index }) => (
                    <ItemCard key={item.key} item={item} section="unsorted" index={index} ctx={ctx} unsorted />
                  ))}
                </DayCard>
              )}

              {waitingCard}

              <FoldedCard
                label={quiet ? 'Folded mail' : 'Folded'}
                ctx={ctx}
                open={openPile}
                onToggle={(k) => setOpenPile((cur) => (cur === k ? null : k))}
                piles={foldPiles(t!, foldItems, important, quiet, today)}
                footer={
                  quiet
                    ? [lastSort ? `sorted by Claude at ${clock24(lastSort)}` : null]
                    : ['piles show the last 7 days', lastSort ? `sorted by Claude at ${clock24(lastSort)}` : null, 'nothing is written back to Gmail']
                }
              />

              {weeklyReady && audit.data && <WeeklyCard items={audit.data.sample.length} to={scenario ? `/day/weekly?state=${scenario}` : '/day/weekly'} />}
            </>
          )}
        </div>

        <aside className="flex min-w-0 max-w-[420px] flex-[1_1_320px] flex-col gap-[18px]">
          {!loadingMail &&
            stale.map((c) => (
              <StaleBanner key={c.key} name={c.name} asOf={c.value} error={accounts.find((a) => a.id === c.key)?.lastError ?? null} />
            ))}
          {yourDay}
          {firstRun ? (
            <p className="m-0 px-1 text-[11px] leading-[1.5] text-microcopy">Classes, deadlines and reminders already show here. Mail joins them once an inbox is connected.</p>
          ) : (
            comingCard
          )}
        </aside>
      </main>

      <UndoToast toast={ctrl.toast} ctrl={ctrl} />
      <ShortcutSheet open={sheet} onClose={() => setSheet(false)} />
    </div>
  )
}

/** 'Sep 14 at 14:05' — when the last good sync was, in the disconnected banner. */
function sinceWords(ts: string, today: ISODate): string {
  const words = dayClock24(ts, today)
  return words.replace(/^(yesterday|[A-Z][a-z]{2} \d+) /, '$1 at ')
}

function foldPiles(
  t: NonNullable<ReturnType<typeof useDayTriage>['data']>,
  items: { snoozed: DayItem[]; can_wait: DayItem[]; noise: DayItem[] },
  important: DayItem[],
  quiet: boolean,
  today: ISODate,
): FoldPile[] {
  const day = (i: DayItem) => (i.mail ? dayLabelShort(i.mail.receivedAt) : '')
  const piles: FoldPile[] = []
  const snoozedCount = t.counts.snoozed ?? items.snoozed.length
  if (snoozedCount > 0) {
    piles.push({
      key: 'snoozed',
      section: 'snoozed',
      name: 'Snoozed',
      count: snoozedCount,
      summary: t.nextSnoozedAt ? `next ${backLabel(t.nextSnoozedAt, today)}` : null,
      items: items.snoozed,
      rowWhen: (i) => (i.mail?.snoozedUntil ? backLabel(i.mail.snoozedUntil, today) : ''),
    })
  }
  if (quiet && important.length > 0) {
    piles.push({
      key: 'important',
      section: 'important',
      name: 'Important',
      count: important.length,
      summary: foldSummary(important.map((i) => i.mail!)),
      items: important,
      rowWhen: day,
      keyHint: '2',
    })
  }
  piles.push({
    key: 'can_wait',
    section: 'can_wait',
    name: 'Low priority',
    count: t.counts.canWait,
    summary: items.can_wait.length > 0 ? foldSummary(items.can_wait.map((i) => i.mail!)) : null,
    items: items.can_wait,
    rowWhen: day,
    keyHint: quiet ? '3' : undefined,
  })
  piles.push({
    key: 'noise',
    section: 'noise',
    name: 'Noise',
    count: t.counts.noise,
    summary: items.noise.length > 0 ? foldSummary(items.noise.map((i) => i.mail!)) : null,
    items: items.noise,
    rowWhen: day,
    keyHint: quiet ? '4' : undefined,
  })
  return piles
}

/** 'Oct 4' — a folded row's day. */
function dayLabelShort(ts: string): string {
  const d = new Date(ts)
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]} ${d.getDate()}`
}

function firstRunSources(connectors: { id: string; status: string; lastSyncAt: string | null }[], courses: number | null, today: ISODate): FirstRunSource[] {
  const canvas = connectors.find((c) => c.id === 'canvas')
  const gcal = connectors.find((c) => c.id === 'gcal')
  const live = (c?: { status: string }) => Boolean(c && c.status !== 'disconnected' && c.status !== 'planned')
  const last = (c?: { lastSyncAt: string | null }) => (c?.lastSyncAt ? `last sync ${dayClock24(c.lastSyncAt, today)}` : 'not synced yet')
  return [
    { key: 'professional', title: 'Professional', src: 'Gmail · read-only', kind: 'inbox', connected: false },
    { key: 'berkeley', title: 'Berkeley', src: 'Gmail · read-only', kind: 'inbox', connected: false },
    { key: 'flexible', title: 'Flexible', src: 'Gmail · read-only', kind: 'inbox', connected: false },
    {
      key: 'bcourses',
      title: 'bCourses',
      src: live(canvas) ? `${courses !== null ? `${courses} courses · ` : ''}${last(canvas)}` : 'Not connected',
      kind: 'bcourses',
      connected: live(canvas),
    },
    { key: 'calendar', title: 'Google Calendar', src: live(gcal) ? `read-only mirror · ${last(gcal)}` : 'Not connected', kind: 'calendar', connected: live(gcal) },
  ]
}
